import { env } from '@/app/config/env';

/**
 * Turns whatever a master's image field holds into something a browser can render.
 *
 * <h2>The three shapes a stored image arrives in</h2>
 *
 * 1. **A data URI** — `data:image/png;base64,…`. What Organization Setup writes and what the `@Base64Image`
 *    validated columns hold today. Renderable as-is.
 * 2. **An absolute URL** — `https://…`. Renderable as-is.
 * 3. **A server-relative path** — `/files/abc.png` or `files/abc.png`. Renderable only once joined to the
 *    API's own base, which is the one piece of knowledge a component should not carry.
 *
 * <p>Anything else — a bare file name, an empty string, a Windows path — is **not** a URL, and this returns
 * `null` for it rather than handing the browser something that will 404 into a broken-image icon. A caller
 * that gets `null` shows the value as text, which is at least true.
 *
 * <p>This is deliberately narrow about what it will treat as a path: it must never emit a `file://`, a
 * `C:\…` or any other local-filesystem location, because those are the server's internals and a page that
 * requests one leaks where the server keeps its files.
 */

const DATA_URI = /^data:image\/[a-z0-9.+-]+;base64,/i;
const ABSOLUTE = /^https?:\/\//i;
/* A path, not a name: it has a slash and a recognisable image extension. */
const RELATIVE_IMAGE = /^\/?(?:[\w.~-]+\/)+[\w.~-]+\.(?:png|jpe?g|gif|webp|svg|avif)$/i;

export function toMediaUrl(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;

  const trimmed = value.trim();
  if (trimmed === '') return null;

  if (DATA_URI.test(trimmed) || ABSOLUTE.test(trimmed)) return trimmed;

  if (RELATIVE_IMAGE.test(trimmed)) {
    const base = env.API_BASE_URL.replace(/\/+$/, '');
    const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return `${base}${path}`;
  }

  return null;
}

/** Whether a value is something {@link toMediaUrl} can render, for a caller choosing between two renderings. */
export function isRenderableImage(value: string | null | undefined): boolean {
  return toMediaUrl(value) !== null;
}
